<template>
  <div :class="actionButtonWrapperClasses.join(' ')">
    <div class="action-modal-wrapper" v-if="showModal">
      <div class="action-modal-container">
        <div class="m-header">
          <div class="header">
            <h2>{{ headline }}</h2>
          </div>
        </div>
        <div class="m-body">
          <div v-if="dateTime">
            <date-picker
              v-model="dateSelected"
              lang="en"
              type="datetime"
              width="480"
              input-class="date-time-picker"
              format="MM/DD/YYYY hh:mm A"
              :confirm="true"
              :clearable="false"
              @confirm="inputConfirmed"
              :not-before="lowestLaunchTime"
              :time-picker-options="{
                start: '00:00',
                step: '00:60',
                end: '24:00',
              }"
            >
            </date-picker>
            <br />
            <p class="text-white">
              Time zone: <b>{{ formattedTimezone }}</b>
            </p>
            <p class="date-time-caption">Click on the date above to change it.</p>
          </div>
          <slot></slot>
          <div class="col-md-6 col-md-offset-3" v-if="inputConfirmation">
            <div class="col-md-12" style="font-size: 21px; color: white; padding-bottom: 20px">
              {{ inputConfirmationMessage }} Please type "{{ actionName.toUpperCase() }}" below and click the '{{ buttonLabel }}' button to confirm.
              <span v-if="actionName.toUpperCase() === 'UNSUBSCRIBE ALL'">Please note that this will also affect any pending subscribers.</span>
            </div>
            <input
              class="col-md-12"
              type="text"
              style="border-radius: 5px"
              placeholder="enter confirmation text"
              id="confirm-input"
              required
            />
            <div
              v-if="!processing && confirmationError"
              class="col-md-12"
              style="padding: 10px; font-size: 18px; color: red; padding-bottom: 20px"
            >
              Error: To confirm you want to {{ actionName.toLowerCase() }} of your {{ actionParams.table.toLowerCase() }}, type '{{ actionName.toUpperCase() }}'
              above and try again.
            </div>
          </div>
          <button
            v-if="!actionButtonHidden"
            id="actionButton"
            @click="takeAction"
            :class="'btn btn-danger btn-lg modal-action-button ' + buttonType"
            :disabled="countdown"
          >
            {{ (scheduleButtonLabel) ? scheduleButtonLabel : buttonLabel }}
            <span> {{ countdownTimerLabel }}</span>
          </button>

          <button
              v-if="launchImmediatelyButton && immediateActionUrl"
              id="immediateActionButton"
              @click="confirm = true"
              :class="'btn-launch-immediately modal-action-button'"
              :disabled="countdown"
          >
            Click here to launch immediately!
            <span style="position: absolute; top: 9px; left: 333px;">{{ countdownTimerLabel }}</span>
          </button>

          <modal-wrapper v-if="launchImmediatelyButton && immediateActionUrl" :shown="confirm" title="Launch the campaign right now?" @close-modal="confirm = false">
            <p class="text-white">
              If you confirm, the campaign will begin delivering right away.<br/>
              If you meant to schedule the campaign for the future, click the X in the top right.
            </p>

            <button
                :disabled="processing"
                class="btn btn-warning btn-lg modal-action-button"
                type="button"
                @click="takeImmediateAction"
            >
              Confirm
            </button>
            <div v-if="processing" class="processing-indicator text-white">Please wait while we process your request ...</div>
          </modal-wrapper>

          <div v-if="processing" class="processing-indicator">Please wait while we process your request ...</div>
        </div>
        <div
          class="close-modal"
          @click="
            showModal = false;
            headerElem.style.display = 'block';
            $emit('close-modal');
          "
        >
          x
        </div>
      </div>
    </div>
    <span
      v-tooltip="disableButton && {content: disableButtonTooltip, trigger: 'hover', placement: 'bottom', offset: 15}"
    >
      <button
        v-if="!actionButtonStyles"
        @click="handleShowModal"
        :class="
          'action-modal-button btn btn-' +
          buttonType +
          ' pull-' +
          (floatRight ? 'right' : 'left') +
          ' ' +
          customButtonClasses.join(' ')
        "
        :disabled="processing || disableButton"
      >
        {{ buttonLabel }}
      </button>
      <button
        v-else-if="openModalButtonClasses.length > 0"
        @click="handleShowModal"
        :class="openModalButtonClasses.join(' ')"
        :disabled="processing || disableButton"
      >
        {{ buttonLabel }}
      </button>
      <button v-else @click="handleShowModal" class="actionButton" :disabled="processing || disableButton">
        {{ buttonLabel }}
      </button>
      <div v-if="processing" class="processing-indicator">Please wait while we process your request ...</div>
    </span>
  </div>
</template>

<script>
import * as Sentry from "@sentry/vue";
import axios from 'axios';
import querystring from 'querystring';
import DatePicker from 'vue2-datepicker';
import {VTooltip} from 'v-tooltip';
import {convertLocalDateToServerMoment } from '../utility/date/date-converter';
import ModalWrapper from "./modal/ModalWrapper.vue";

export default {
  name: 'action-modal',
  methods: {
    takeAction() {
      if (this.inputConfirmation && !this.actionConfirmed()) {
        this.confirmationError = true;
        return false;
      }

      if (!!this.takeActionFunction) {
        this.takeActionFunction();
        return false;
      }

      this.processing = true;
      document.getElementById('actionButton').disabled = true;

      this.$emit('action-processing', {
        type: this.processType,
      });

      if (this.dateTime && this.actionParams.launchdate === null) this.setLaunch();

      let self = this;

      if (this.waitForPusherNotification) {
        this.listenToPusherNotification();
      }

      axios
        .post(self.actionUrl, self.stringifyParams ? querystring.stringify(self.actionParams) : self.actionParams)
        .then(response => {
          if (this.handleErrors(response.data)) {
            this.showModal = false;
            this.headerElem.style.display = 'block';
            location.reload();
            return false;
          }
          if (!this.waitForPusherNotification) {
            this.defaultReaction();
          }
        })
        .catch(error => {
          const {data} = error.response;
          this.showModal = false;
          this.headerElem.style.display = 'block';
          if (!this.handleErrors(data)) {
            $.notify('Request failed! Try again later.', 'error');
            Sentry.captureException(error);
          }
          location.reload();
          return false;
        });
    },
    takeImmediateAction() {

      this.processing = true;
      document.getElementById('immediateActionButton').disabled = true;

      let self = this;

      axios
          .post(self.immediateActionUrl, self.stringifyParams ? querystring.stringify(self.actionParams) : self.actionParams)
          .then(response => {
            if (this.handleErrors(response.data)) {
              this.showModal = false;
              this.headerElem.style.display = 'block';
              this.processing = false;
              return false;
            }
            if (!this.waitForPusherNotification) {
              this.defaultReaction();
            }
          })
          .catch(error => {
            const {data} = error.response;
            this.showModal = false;
            this.headerElem.style.display = 'block';
            if (!this.handleErrors(data)) {
              $.notify('Request failed! Try again later.', 'error');
              Sentry.captureException(error);
            }
            location.reload();
            return false;
          });
    },
    handleErrors(data) {
      if (data && Array.isArray(data.validation_errors) && data.validation_errors.length) {
        self.$emit('vue-app-errors', data.validation_errors);
        $.notify('Please correct errors and try again!', 'warning');
        return true;
      } else if (data && data.errors) {
        if (data.errors.launch) {
          $.notify(data.errors.launch, 'warning');
        }
        else if (data.errors.inactive_agent) {
          $.notify('You must enter an active agent code.', 'warning');
        } else if (data.errors.no_plan) {
          $.notify('You must subscribe to a plan. Redirecting to plans page.', 'warning');
          setTimeout(() => {
            window.location = '/plans';
          }, 1500);
        } else {
          $.notify(JSON.stringify(data.errors), 'warning');
        }
        return true;
      }
      return false;
    },
    defaultReaction () {
      this.showModal = false;
      this.headerElem.style.display = 'block';

      $.notify('Success! Redirecting...', 'success');
      this.$emit('close-modal');

      setTimeout(() => {
        window.location = this.redirectUrl;
      }, 1500);
    },
    formatDate(date) {
      if (typeof date === 'string') {
        return date;
      }
      return moment(date).format('MM/DD/YYYY HH:mm');
    },
    inputConfirmed() {
      this.setLaunch();
    },
    actionConfirmed() {
      let inputEl = document.getElementById('confirm-input');
      return inputEl.value === this.actionName.toUpperCase();
    },
    handleShowModal(e) {
      if (e.screenX !== 0) {
        if (this.buttonCountdownTimer) {
          // reset countdown
          clearInterval(this.countdownTimer);

          let time_left = this.buttonCountdownTimer;

          this.countdownTimerLabel = '(' + time_left + ')';
          this.countdown = true;

          this.countdownTimer = setInterval(() => {
            time_left -= 1;

            if (time_left === 0) {
              // Finished
              clearInterval(this.countdownTimer);

              this.countdownTimerLabel = '';
              this.countdown = false;
            } else {
              // Countdown still going
              this.countdownTimerLabel = '(' + time_left + ')';
            }
          }, 1000);
        }

        this.showModal = true;
        this.headerElem.style.display = 'none';
      }
    },
    listenToPusherNotification() {
      window.addEventListener('message', ({data}) => {
        if (data && data.type === 'pusherNotification' && data.notification && data.notification.message && data.notification.message.text === this.waitForPusherNotification) {
          this.defaultReaction();
        }
      });
    },
    setLaunch() {
      if (!this.dateTime) {
        return;
      }
      this.actionParams.launchdate = convertLocalDateToServerMoment(
          this.formatDate(this.dateSelected),
          'MM/DD/YYYY HH:mm',
          this.timezone || moment.tz.guess()
      ).format('YYYY/MM/DD HH:mm');
      this.$emit('date-selected', this.actionParams.launchdate);
    }
  },
  components: {ModalWrapper, VTooltip, DatePicker},
  props: {
    headline: {
      required: true,
      type: String,
    },
    buttonLabel: {
      required: true,
      type: String,
    },
    scheduleButtonLabel: {
      required: false,
      type: String,
    },
    buttonType: {
      required: true,
      type: String,
    },
    actionName: {
      required: false,
      type: String,
      default: 'Delete All',
    },
    actionUrl: {
      required: false,
      type: String,
    },
    actionParams: {
      required: false,
      type: Object,
    },
    actionButtonHidden: {
      required: false,
      type: Boolean,
      default: false,
    },
    redirectUrl: {
      required: false,
      type: String,
    },
    takeActionFunction: {
      required: false,
      type: Function,
    },
    floatRight: {
      required: false,
      type: Boolean,
      default: false,
    },
    dateTime: {
      required: false,
      type: Boolean,
      default: false,
    },
    timezone: {
      required: false,
      type: String,
      default: '',
    },
    actionButtonStyles: {
      required: false,
      type: Boolean,
      default: false,
    },
    customButtonClasses: {
      required: false,
      type: Array,
      default: () => [],
    },
    openModalButtonClasses: {
      required: false,
      type: Array,
      default: () => [],
    },
    disableButton: {
      required: false,
      type: Boolean,
      default: false,
    },
    disableButtonTooltip: {
      required: false,
      type: String,
    },
    inputConfirmation: {
      required: false,
      type: Boolean,
      default: false,
    },
    inputConfirmationMessage: {
      required: false,
      type: String,
    },
    actionButtonWrapperClasses: {
      required: false,
      type: Array,
      default: () => [],
    },
    processType: {
      required: false,
      type: String,
    },
    stringifyParams: {
      type: Boolean,
      default: true,
    },
    buttonCountdownTimer: {
      required: false,
      type: Number,
    },
    waitForPusherNotification: {
      required: false,
      type: String,
    },
    launchImmediatelyButton: {
      required: false,
      type: Boolean,
      default: false,
    },
    immediateActionUrl: {
      required: false,
      type: String,
    },
  },
  data() {
    return {
      headerElem: document.getElementById('masthead'),
      showModal: false,
      processing: false,
      dateSelected: null,
      confirmationError: false,
      countdown: false,
      countdownTimer: null,
      countdownTimerLabel: '',
      confirm: false
    };
  },
  computed: {
    lowestLaunchTime() {
      if (!this.dateTime) {
        return null;
      }
      return this.actionParams.resumedate ?? this.currentTime;
    },
    currentTime() {
      if (this.timezone) {
        return moment.tz(this.timezone).add(1, 'hour').minutes(0).format('MM/DD/YYYY HH:mm');
      } else {
        return moment().add(1, 'hour').minutes(0).format('MM/DD/YYYY HH:mm');
      }
    },
    formattedTimezone() {
      return (this.timezone || moment.tz.guess()).replace('_', ' ');
    },
  },
  watch: {
    lowestLaunchTime: {
      immediate: true,
      handler(lowestLaunchTime) {
        if (!this.dateTime) {
          return;
        }
        this.dateSelected = this.formatDate(lowestLaunchTime);
        this.setLaunch();
      },
    },
  },
};
</script>

<style>
.action-modal-wrapper {
  background: rgb(102, 204, 204);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000000;
  height: 100%;
  width: 100%;
}

.action-modal-container {
  margin: 0 auto;
  max-width: 960px;
  width: 100%;
  height: 100%;
  padding: 100px 0 0 0;
}

.action-modal-container .mx-datepicker-popup {
  left: calc(50% - 125px) !important;
}

.header h2 {
  color: #fff;
  text-align: center;
}

#reschedule-date {
  background: transparent;
  border: 0;
  font-size: 40px;
  font-weight: bold;
  color: #ffffff;
  text-align: center;
}

.close-modal {
  position: absolute;
  top: 0;
  right: 35px;
  color: #777;
  font-size: 50px;
  font-weight: bold;
  cursor: pointer;
  transition: 350ms ease all;
}

.close-modal:hover {
  color: #333;
}

.modal-action-button {
  margin-top: 40px;
  padding: 10px;
  font-size: 150%;
  width: 100%;
  max-width: 500px;
  transition: 350ms ease all;
}

.modal-action-button.danger {
  background: #d9534f;
  border-color: #d43f3a;
}

.modal-action-button.danger:hover {
  background: #973937;
}

.modal-action-button.warning {
  background: #f0ad4e;
  border-color: #eea236;
}

.modal-action-button.warning:hover {
  background: #e4b52f;
}

.date-time-caption {
  color: #fff;
  font-style: italic;
  font-weight: bold;
  font-size: 16px;
}

.action-modal-button {
  display: block;
  margin-right: 10px;
  padding: 10px 25px;
  transition: 350ms ease all;
}

.btn-launch-immediately {
  margin-top: 30px;
  background: none;
}

.btn-launch-immediately:hover {
  background: none;
  opacity: 0.7;
}

.processing-indicator {
  float: left;
  width: 100%;
  font-size: 24px;
  padding: 5px 0;
}

.m-body {
  text-align: center;
}

.display-inline-block {
  display: inline-block;
}

.display-block {
  display: block;
}

.text-white {
  color: #fff;
}

#actionButton {
  position: relative;
}
</style>
